import { Box, InputAdornment, TextField, Typography, styled } from '@mui/material';
import React, { useEffect } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { hideScrollbarStyles } from '../../utils/styles';
import {
  getNotificationsRequest,
  INotification,
  WebNotificationType,
} from '../../redux/notification/notificationSlice';
import CoachPageLayout from '../../components/CoachPageLayout/CoachPageLayout';
import NotificationMessageCard from '../../components/Notification/NotificationMessageCard';
import { CLIENT_ACTIVITIES_ROUTE } from '../../utils/routes';

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    height: '36px',
    width: '538px',
    backgroundColor: theme.palette.primary.light,
  },
  '& .MuiInputBase-input': {
    ...theme.typography.bodyRegular,
  },
  '& fieldset': {
    border: 'none',
  },
}));

function Notification() {
  const { t, i18n } = useTranslation();
  const appLanguage = i18n.language;
  const notifications = useAppSelector((state) => state.notification.notifications);

  const coach = useAppSelector((state) => state.coach.coach);
  const coachId = coach?.id || '';
  const dispatch = useAppDispatch();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [debouncedSearchQuery] = useDebounce(searchQuery, 1000);
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(
      getNotificationsRequest({
        coachId,
        skip: 0,
        take: 1000,
        searchQuery: debouncedSearchQuery,
        isViewed: undefined,
      })
    );
  }, [dispatch, coachId, notifications, debouncedSearchQuery]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleActivityNavigation = (id: string) => {
    navigate(
      CLIENT_ACTIVITIES_ROUTE.replace(':id', id)
        .replace('/:groupid?', '')
        .replace('/:groupname?', '')
    );
  };

  const groupedNotifications = notifications.reduce((acc, notification) => {
    const date = dayjs(notification.createdAt).format('YYYY-MM-DD');
    const updatedAcc = { ...acc };
    updatedAcc[date] = [...(updatedAcc[date] || []), notification];
    return updatedAcc;
  }, {});

  return (
    <CoachPageLayout>
      <Box>
        <Box
          width="586px"
          margin="20px auto"
          bgcolor="white"
          height="calc(100vh - 160px)"
          py="24px"
        >
          <Box display="flex" pb="24px" px="24px">
            <StyledTextField
              placeholder={`${t('common.search')}...`}
              onChange={handleSearchChange}
              value={searchQuery}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <img src="/assets/icons/search.svg" alt="search" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box
            overflow="auto"
            sx={hideScrollbarStyles}
            bgcolor="white"
            height="100%"
            width="100%"
            px="24px"
            display="flex"
            flexDirection="column"
            gap="27px"
          >
            {Object.entries(groupedNotifications).length === 0 ? (
              <Typography variant="h6" color="textSecondary" textAlign="center">
                {t('notifications.noNotifications')}
              </Typography>
            ) : (
              Object.entries(groupedNotifications).map(([groupedDate, latestNotifications]) => {
                const formattedDate = (date: string): string => {
                  const today = dayjs();
                  const yesterday = today.subtract(1, 'day');

                  // Check for "today" first for potential performance optimization
                  if (dayjs(date).isSame(today, 'day')) {
                    return t('notifications.today');
                  }
                  if (dayjs(date).isSame(yesterday, 'day')) {
                    return t('notifications.yesterday');
                  }
                  return dayjs(date).format('D MMMM YYYY');
                };
                return (
                  <Box key={groupedDate} display="flex" flexDirection="column" gap="20px">
                    <Typography
                      variant="meta"
                      color="textSecondary"
                      fontSize={16}
                      fontWeight={500}
                      textAlign={appLanguage === 'ar' ? 'right' : 'left'}
                    >
                      {formattedDate(groupedDate)}
                    </Typography>
                    {(latestNotifications as INotification[]).map((notification, index) =>
                      notification ? (
                        <NotificationMessageCard
                          key={Math.random().toString(36)}
                          index={index}
                          totalItems={(latestNotifications as INotification[]).length}
                          message={{
                            status: notification.status,
                            title: notification.title,
                            message: notification.description,
                            time: notification.createdAt,
                            profilePic: notification.client?.profilePic,
                          }}
                          onClick={() =>
                            notification.type === WebNotificationType.ACTIVITY
                              ? handleActivityNavigation(notification.client?.id)
                              : {}
                          }
                        />
                      ) : null
                    )}
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      </Box>
    </CoachPageLayout>
  );
}
export default Notification;
